import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, of, map, catchError, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../api/notification.service';
import { ChatService } from '../api/chat.service';
import { ArticlesService } from '../api/articles.service';
import { AppStorageService } from '../app-storage.service';
import { StorageKeys } from '../storage-keys';
import { PremiumChatService } from '../api/premium-chat.service';
import {
  normalizeNotificationType,
  buildRecoveryNotificationHtml,
  extractConversationId,
  extractNotificationRoute,
} from './notification.utils';

/**
 * Normalized notification model used by both app and web views.
 */
export interface MappedNotification {
  id: number;
  text: string;
  safeBody: SafeHtml | null;
  title?: string;
  date: string;
  time: string;
  type: number | null;
  viewed: boolean;
  route: string | null;
  /** Original API payload for operations that need raw data */
  originalData: any;
}

/**
 * Result of starting a contact/conversation from a notification.
 */
export interface StartContactResult {
  success: boolean;
  conversationId: number | null;
  ownerNotified: boolean;
}

export interface RecoveryDialogState {
  conversationId: number;
  shouldOpenDialog: boolean;
  premiumChatAmount: number;
  ownerFreeMessage: string;
  ownerFreeMsgConsumed: boolean;
  finderFreeMessage: string;
}

/**
 * Centralizes all notification business logic shared between
 * the mobile notifications page and the web notifications component.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationActionsService {
  constructor(
    private notificationService: NotificationService,
    private chatService: ChatService,
    private articlesService: ArticlesService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private storage: AppStorageService,
    private premiumChatService: PremiumChatService,
  ) {}

  /**
   * Maps a raw API notification to the unified MappedNotification format.
   * Date/time formatting is left to the caller since it depends on locale strategy
   * (DatePipe vs toLocaleString).
   */
  mapNotification(raw: any, dateLabel: string = '', timeLabel: string = ''): MappedNotification {
    const bodyRaw: string = String(raw?.body ?? raw?.text ?? '');
    const type: number | null = normalizeNotificationType(raw);

    let safeBody: SafeHtml | null = null;
    if (type === 5 || type === 6) {
      const html: string = type === 6
        ? buildRecoveryNotificationHtml(bodyRaw)
        : bodyRaw;
      safeBody = this.sanitizer.bypassSecurityTrustHtml(html);
    }

    return {
      id: raw?.id,
      text: bodyRaw,
      safeBody,
      title: raw?.title ?? undefined,
      date: dateLabel,
      time: timeLabel,
      type,
      viewed: !!(raw?.is_read ?? raw?.viewed),
      route: extractNotificationRoute(raw),
      originalData: raw,
    };
  }

  /**
   * Marks a notification as read via the API and updates local state.
   */
  markAsReadAndUpdate(notification: MappedNotification, _notifications: MappedNotification[]): void {
    if (notification.viewed) return;

    this.notificationService.markAsRead(notification.id).subscribe({
      next: () => {
        notification.viewed = true;
      },
      error: () => {},
    });
  }

  /**
   * Deletes a notification at the given index from the API and removes it locally.
   */
  deleteAndRemove(index: number, notifications: MappedNotification[]): void {
    const notification: MappedNotification = notifications[index];
    if (!notification) return;

    this.notificationService.deleteNotification(notification.id, notification.viewed).subscribe({
      next: () => {
        notifications.splice(index, 1);
      },
      error: () => {},
    });
  }

  /**
   * Deletes a specific notification by ID from the API and removes it from the local array.
   * Used after starting contact or sending proposal to clean up the originating notification.
   */
  deleteOriginatingNotification(notifId: number, viewed: boolean, notifications: MappedNotification[]): void {
    this.notificationService.deleteNotification(notifId, viewed).subscribe({
      next: () => {
        const idx: number = notifications.findIndex((n: MappedNotification) => n.id === notifId);
        if (idx > -1) notifications.splice(idx, 1);
      },
      error: () => {},
    });
  }

  /**
   * Checks whether the conversation linked to a notification is accessible.
   * Returns Observable<boolean>: true if the chat can be opened, false otherwise.
   * Only applies to type 4 and 6 notifications without suppress_open_action.
   */
  resolveConversationAccess(notification: any): Observable<boolean> {
    const type: number | null = normalizeNotificationType(notification);

    if ((type !== 4 && type !== 6) || notification?.data?.suppress_open_action) {
      return of(false);
    }

    const conversationId: number | null = extractConversationId(notification);
    if (!conversationId) return of(false);

    return this.chatService.loadChat(conversationId).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  /**
   * Starts a conversation from a type 5 notification.
   */
  startContact(articleId: number, interestedUserId: number): Observable<StartContactResult> {
    return this.chatService.startConversation(articleId, interestedUserId).pipe(
      map((res: any) => ({
        success: !!(res?.success && res?.conversation_id),
        conversationId: res?.conversation_id ?? null,
        ownerNotified: !!res?.owner_notified,
      })),
    );
  }

  /**
   * Resolves whether a notification-linked conversation should open the
   * recovery-options dialog locally or navigate directly to chat.
   */
  loadRecoveryDialogState(notificationOrConversationId: any): Observable<RecoveryDialogState | null> {
    const conversationId = this.resolveConversationId(notificationOrConversationId);
    if (!conversationId) {
      return of(null);
    }

    return this.premiumChatService.getAmount(conversationId).pipe(
      switchMap((status) => {
        const premiumChatUnlocked = status?.premium_chat_unlocked ?? true;
        const premiumChatAmount = Number(status?.amount) || 0;

        if (premiumChatUnlocked) {
          return of(this.buildEmptyRecoveryDialogState(conversationId, premiumChatAmount, false));
        }

        return this.chatService.getChat(conversationId).pipe(
          map((result: any) => this.buildRecoveryDialogStateFromChat(conversationId, premiumChatAmount, result)),
          catchError(() => of(this.buildEmptyRecoveryDialogState(conversationId, premiumChatAmount, true))),
        );
      }),
      catchError(() => this.chatService.getChat(conversationId).pipe(
        map((result: any) => {
          const premiumChatUnlocked = result?.premium_chat_unlocked ?? true;
          const premiumChatAmount = Number(result?.premium_chat_amount) || 0;

          if (premiumChatUnlocked) {
            return this.buildEmptyRecoveryDialogState(conversationId, premiumChatAmount, false);
          }

          return this.buildRecoveryDialogStateFromChat(conversationId, premiumChatAmount, result);
        }),
        catchError(() => of(this.buildEmptyRecoveryDialogState(conversationId, 0, false))),
      )),
    );
  }

  /**
   * Sends the owner's free recovery message and returns the stored content.
   */
  sendRecoveryMessage(conversationId: number, message: string): Observable<string> {
    const trimmed = message?.trim() ?? '';
    if (!trimmed) {
      return of('');
    }

    return this.chatService.sendMessage(conversationId, { message: trimmed }).pipe(
      map((response: any) => response?.data?.content || trimmed),
    );
  }

  /**
   * Sends the finder's response to the owner's proposal (accept proposal flow).
   */
  sendProposalResponse(conversationId: number, responseText: string, defaultMessage: string): Observable<void> {
    const trimmed: string = responseText?.trim() ?? '';
    const messageToSend: string = trimmed || defaultMessage;

    return this.chatService.sendMessage(conversationId, { message: messageToSend }).pipe(
      map(() => void 0),
    );
  }

  /**
   * Hydrates a proposal notification with the latest response already sent by the
   * current finder, so stale notifications do not offer a second free reply.
   */
  hydrateProposalResponse(notification: any): Observable<any> {
    if (!this.isProposalNotificationSource(notification)) {
      return of(notification);
    }

    const conversationId: number | null = extractConversationId(notification);
    if (!conversationId) {
      return of(notification);
    }

    const currentUserId = Number(this.storage.getJSON<any>(StorageKeys.USER)?.id);
    if (!Number.isFinite(currentUserId) || currentUserId <= 0) {
      return of(notification);
    }

    return this.chatService.getChat(conversationId).pipe(
      map((result: any) => {
        const messages = Array.isArray(result?.messages) ? result.messages : [];
        const finderMessages = messages
          .filter((message: any) => Number(message?.sender_id) === currentUserId)
          .map((message: any) => String(message?.content ?? '').trim())
          .filter((content: string) => content.length > 0);
        const latestFinderResponse = finderMessages[finderMessages.length - 1] ?? '';

        if (!latestFinderResponse) {
          return notification;
        }

        return this.attachProposalResponse(notification, latestFinderResponse);
      }),
      catchError(() => of(notification)),
    );
  }

  /**
   * Preloads an article from a route like /details/:id.
   * Returns the article object or null if not found.
   */
  preloadArticle(route: string): Observable<any | null> {
    const articleId: number | null = this.extractRouteId(route);
    if (!articleId) return of(null);

    return this.articlesService.getArticleById(articleId).pipe(
      map((response: any) => {
        if (response?.success && response?.article) {
          return response.article;
        }
        return null;
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * Extracts a numeric ID from a /details/:id route.
   */
  private extractRouteId(route: string): number | null {
    const match: RegExpMatchArray | null = route.match(/\/details\/(\d+)/);
    if (!match) return null;

    const id: number = Number(match[1]);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private resolveConversationId(notificationOrConversationId: any): number | null {
    if (typeof notificationOrConversationId === 'number') {
      return Number.isFinite(notificationOrConversationId) && notificationOrConversationId > 0
        ? notificationOrConversationId
        : null;
    }

    return extractConversationId(notificationOrConversationId);
  }

  private isProposalNotificationSource(notification: any): boolean {
    const source = notification?.originalData ?? notification;

    return normalizeNotificationType(source) === 4
      && source?.data?.suppress_open_action === true;
  }

  private attachProposalResponse(notification: any, responseText: string): any {
    notification.proposalResponseText = responseText;

    if (notification?.data && typeof notification.data === 'object') {
      notification.data.proposalResponse = responseText;
      notification.data.proposal_response = responseText;
    }

    if (notification?.originalData && typeof notification.originalData === 'object') {
      notification.originalData.proposalResponseText = responseText;
      notification.originalData.proposalResponse = responseText;
      notification.originalData.proposal_response = responseText;

      if (notification.originalData.data && typeof notification.originalData.data === 'object') {
        notification.originalData.data.proposalResponse = responseText;
        notification.originalData.data.proposal_response = responseText;
      }
    }

    return notification;
  }

  private buildRecoveryDialogStateFromChat(
    conversationId: number,
    premiumChatAmount: number,
    result: any,
  ): RecoveryDialogState {
    const currentUserId = Number(this.storage.getJSON<any>(StorageKeys.USER)?.id);
    const messages = Array.isArray(result?.messages) ? result.messages : [];
    const ownerMessages = messages.filter((message: any) => Number(message?.sender_id) === currentUserId);
    const finderMessages = messages.filter((message: any) => Number(message?.sender_id) !== currentUserId);
    const isObjectOwner = result?.is_object_owner ?? true;

    return {
      conversationId,
      shouldOpenDialog: isObjectOwner,
      premiumChatAmount,
      ownerFreeMessage: ownerMessages[ownerMessages.length - 1]?.content || '',
      ownerFreeMsgConsumed: ownerMessages.length > 0,
      finderFreeMessage: finderMessages[finderMessages.length - 1]?.content || '',
    } satisfies RecoveryDialogState;
  }

  private buildEmptyRecoveryDialogState(
    conversationId: number,
    premiumChatAmount: number,
    shouldOpenDialog: boolean,
  ): RecoveryDialogState {
    return {
      conversationId,
      shouldOpenDialog,
      premiumChatAmount,
      ownerFreeMessage: '',
      ownerFreeMsgConsumed: false,
      finderFreeMessage: '',
    };
  }
}
